import React, { useState, useEffect } from "react";
import "../styles/PlayerBackground.css";

/**
 * Componente reutilizable para fondos de reproductores
 * @param {Object} props - Propiedades del componente
 * @param {string} props.backgroundImage - URL de la imagen de fondo
 * @param {string} props.fallbackImage - URL de la imagen de respaldo cuando backgroundImage falla
 * @param {string} props.type - Tipo de reproductor ('v4', 'fixed', 'compact' o 'default')
 * @param {boolean} props.fadeOut - Si debe ocultarse (fundido)
 * @param {string} props.className - Clases adicionales
 * @returns {JSX.Element} Componente de fondo
 */
const PlayerBackground = ({
  backgroundImage,
  fallbackImage,
  type = "default",
  fadeOut = false,
  className = "",
}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Función que verifica si una imagen existe y es accesible con mayor prioridad
  const checkImage = (url) => {
    return new Promise((resolve) => {
      if (!url) {
        resolve(false);
        return;
      }

      const img = new Image();

      // Añadir attributo de fetchpriority para darle alta prioridad a la carga
      img.fetchPriority = "high";

      // Establecer la importancia como alta para navegadores que soportan esta propiedad
      img.importance = "high";

      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  };

  // Efecto para precargar la imagen tan pronto como se monte el componente
  useEffect(() => {
    // Función para precargar la imagen en el navegador
    const preloadImage = (url) => {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = url;
      link.fetchpriority = "high";
      document.head.appendChild(link);
    };

    // Si tenemos la URL de la imagen, precargarla inmediatamente
    if (backgroundImage) {
      preloadImage(backgroundImage);
    }

    // Si hay una imagen de respaldo, también la precargamos pero con menor prioridad
    if (fallbackImage) {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = fallbackImage;
      document.head.appendChild(link);
    }
  }, [backgroundImage, fallbackImage]);

  // Efecto para verificar y establecer la imagen adecuada
  useEffect(() => {
    const loadImage = async () => {
      setIsLoading(true);

      // Intentar cargar la imagen principal
      const mainImageExists = await checkImage(backgroundImage);

      if (mainImageExists) {
        setImageUrl(backgroundImage);
      } else if (fallbackImage) {
        // Si la principal falla, intentar con la imagen de respaldo
        const fallbackImageExists = await checkImage(fallbackImage);
        setImageUrl(fallbackImageExists ? fallbackImage : null);
      } else {
        setImageUrl(null);
      }

      setIsLoading(false);
    };

    loadImage();
  }, [backgroundImage, fallbackImage]);

  return (
    <>
      {/* Añadir un preload directamente en el componente para mayor seguridad */}
      {backgroundImage && (
        <link
          rel="preload"
          as="image"
          href={backgroundImage}
          fetchpriority="high"
          className="hidden"
        />
      )}
      <div
        className={`PlayerBackground ${type} ${fadeOut ? "fadeOut" : ""} ${
          isLoading ? "loading" : "loaded"
        } ${className}`}
        style={{
          backgroundImage: imageUrl ? `url(${imageUrl})` : "none",
        }}
      />
    </>
  );
};

export default PlayerBackground;
